import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { journeyDataActions } from '../../../services/redux/features/journeyData.slice';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from '../components/formInputs';
import { EmploymentDetailsFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';

type OnValuesChange = (values: EmploymentDetailsFormModel_MX) => void;

type Props = {
  formHelpers: FormHelpers;
  onValuesChange: OnValuesChange;
};

export const EmploymentDetailsNew_MX: React.FC<Props> = ({ formHelpers, onValuesChange }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<Partial<EmploymentDetailsFormModel_MX>>>();

  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, []);

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: EmploymentDetailsFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`employmentDetailsForm:title:private`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent onValuesChange={onValuesChange} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  formHelpers: FormHelpers;
  onValuesChange: OnValuesChange;
};

export const FormContent = (props: FormContentProps) => {
  const [inputs] = useState(createFormInputs());

  const { values, submitForm } = useFormikContext<EmploymentDetailsFormModel_MX>();
  const { formHelpers, onValuesChange } = props;
  const { onFormValuesChange } = formHelpers;

  const dispatch = useDispatch();

  useEffect(() => {
    onValuesChange?.(values);
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    onFormValuesChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onValuesChange, values, onFormValuesChange]);

  const disableForm = formHelpers.isDisabledFormSummary;

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.employmentStatus()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.occupation(disableForm)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.employer()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.occupationNotes()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.timeWithEmployerFrom(values.timeWithEmployerTo)}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.timeWithEmployerTo()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.netMonthlyIncome()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.netOtherMonthlyIncome()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.searchAddress()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.county()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
